import { Box, Collapse, Menu, MenuItem } from '@mui/material';
import { removeRateCardEntry, roleRef } from '@timelog/pm-shared';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { parseAmountInput, parsePctInput, useContractEditing, useFinancials } from './editor';
import { formatRateOverCost, metricLabel } from './metricReading';

import { useTimeLogFeature, useTimeLogRates } from '@/api/timelogHooks';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { InlineTextCell } from '@/components/planning/cells';
import { MICRO_LABEL_TRACKING } from '@/components/ui';
import { formatAmount } from '@/lib';
import { roleColor } from '@/lib/roleColor';

/**
 * "Rates per role" — a T&M contract's rate card (BR-11: tasks mapped to this
 * contract price by these rates). New roles come from the tenant's default
 * hourly-rate list (D1 — the role identity is the ServiceID) and arrive
 * seeded with the list's current amount (D3), which the PM then edits to the
 * agreed price. The net column applies `tmDiscountPct`, which is stored and
 * displayed but has no financial effect in v1 (OQ-3).
 *
 * Each row also shows what the role COSTS per hour (read-only — the entry's
 * `costRate`, which the catalogue's stand-in price seeds, ADR 0042) and the
 * uplift the NET rate (after the discount) implies over that cost, in the
 * budget model's metric — the discount is what the customer pays. A card
 * created before cost prices existed shows a dash; "Fill cost prices" copies
 * the catalogue's current prices onto those rows as an explicit edit (BR-13).
 */
export const RateCardEditor = () => {
  const { state, dispatch, budgetModel } = useFinancials();
  const { isEditing, startEditing, stopEditing } = useContractEditing();
  const [open, setOpen] = useState(false);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);

  // The default hourly-rate list is TimeLog's (D1): without the integration there is no role source.
  const feature = useTimeLogFeature();
  const lookupsEnabled = feature !== undefined && feature !== 'off';
  // Fetched while the table is open too: the fill-costs action needs the catalogue.
  const roleLookup = useTimeLogRates(open || addAnchor !== null);

  const { rateCard, tmDiscountPct } = state.document;
  const isTm = state.type === 'tm';
  const discount = tmDiscountPct ?? 0;
  const metric = budgetModel.config.metric;

  const addableRoles = (roleLookup.data?.items ?? []).filter(
    (item) => !rateCard.some((entry) => entry.roleRef.tlpId === item.serviceId),
  );

  /** The rate after the T&M discount — the Net column, and the basis of the factor/margin column. */
  const netRate = (rate: number): number => (isTm ? Math.round(rate * (1 - discount / 100)) : rate);
  /**
   * The cost an hour of this row's role is priced at — the entry's, else the
   * snapshot's. A 0 is no price at all (work never costs nothing), so the row
   * reads "—" and "Fill cost prices" offers the catalogue's price for it.
   */
  const costOf = (entry: (typeof rateCard)[number]): number | undefined => {
    const rate = entry.costRate ?? entry.roleRef.snapshot.costRate;
    return rate !== undefined && rate > 0 ? rate : undefined;
  };
  /** Rows with no cost price for which the catalogue currently offers one. */
  const fillable = rateCard.filter(
    (entry) =>
      costOf(entry) === undefined &&
      roleLookup.data?.items.some(
        (item) => item.serviceId === entry.roleRef.tlpId && item.costRate !== undefined,
      ),
  );
  const fillCosts = () =>
    dispatch({
      type: 'APPLY_OP',
      op: (document) => ({
        ...document,
        rateCard: document.rateCard.map((entry) => {
          if (entry.costRate !== undefined || entry.roleRef.snapshot.costRate !== undefined) {
            return entry;
          }
          const costRate = roleLookup.data?.items.find(
            (item) => item.serviceId === entry.roleRef.tlpId,
          )?.costRate;
          return costRate === undefined ? entry : { ...entry, costRate };
        }),
      }),
    });

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        component="button"
        type="button"
        data-automation-id="rate-card-toggle"
        onClick={() => setOpen((value) => !value)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          border: 'var(--border-width-thin) solid var(--border-divider)',
          borderRadius: 'var(--border-radius-pill)',
          bgcolor: open ? 'var(--bg-sunken)' : 'transparent',
          fontSize: 11,
          fontFamily: 'var(--font-family)',
          color: open ? 'var(--fg-1)' : 'var(--fg-3)',
          cursor: 'pointer',
          '&:hover': { borderColor: 'var(--border-subtle)' },
        }}
      >
        <MaterialSymbol name={open ? 'expand_less' : 'expand_more'} size={14} />
        {open ? t('rateCard.Hide') : t('rateCard.Show', { n: rateCard.length })}
      </Box>

      <Collapse in={open}>
        <Box
          data-automation-id="rate-card"
          sx={{
            mt: 1,
            border: 'var(--border-width-thin) solid var(--border-divider)',
            borderRadius: 'var(--border-radius-medium)',
            overflow: 'hidden',
            /*
             * The rate rows are a small table with fixed Rate / Net / remove
             * columns, so they have a hard ~240px floor. The main panel's own
             * floor keeps them fitting, but the window can still be narrower than
             * that; scrolling the table is better than pushing the card wider than
             * its grid track and clipping it.
             */
            overflowX: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              bgcolor: 'var(--bg-sunken)',
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: MICRO_LABEL_TRACKING,
              textTransform: 'uppercase',
              color: 'var(--fg-3)',
            }}
          >
            <Box sx={{ flex: 1 }}>{t('pmCommon:term.Role')}</Box>
            <Box
              sx={{ width: 80, textAlign: 'right' }}
              title={t('rateCard.Columns.CostPerHourTitle')}
            >
              {t('rateCard.Columns.CostPerHour')}
            </Box>
            <Box sx={{ width: 80, textAlign: 'right' }}>{t('rateCard.Columns.RatePerHour')}</Box>
            {isTm ? (
              <Box
                sx={{
                  width: 110,
                  textAlign: 'right',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                }}
              >
                {t('rateCard.Columns.NetPrefix')}
                <InlineTextCell
                  automationId="rate-card-discount"
                  value={String(discount)}
                  editing={isEditing('tm-discount')}
                  onStartEdit={() => startEditing('tm-discount')}
                  onCancel={stopEditing}
                  onCommit={(raw) => {
                    const pct = parsePctInput(raw);
                    if (pct !== null) {
                      dispatch({
                        type: 'APPLY_OP',
                        op: (document) => ({ ...document, tmDiscountPct: pct }),
                      });
                    }
                    stopEditing();
                  }}
                  inputWidth={30}
                  align="right"
                  display={<Box component="span">{discount}%</Box>}
                />
                )
              </Box>
            ) : null}
            <Box
              sx={{ width: 64, textAlign: 'right' }}
              title={
                metric === 'factor'
                  ? t('rateCard.Columns.MetricTitleFactor')
                  : t('rateCard.Columns.MetricTitleMargin')
              }
            >
              {metricLabel(metric, t)}
            </Box>
            <Box sx={{ width: 18 }} />
          </Box>

          {rateCard.map((entry) => (
            <Box
              key={entry.id}
              data-automation-id="rate-card-row"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 1,
                borderTop: 'var(--border-width-thin) solid var(--border-divider)',
              }}
            >
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box
                  component="span"
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: roleColor(entry.roleRef.tlpId),
                    flex: 'none',
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    color: 'var(--fg-1)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {entry.roleRef.snapshot.name}
                </Box>
              </Box>
              <Box
                data-automation-id="rate-card-cost"
                title={costOf(entry) === undefined ? t('rateCard.NoCostTitle') : undefined}
                sx={{
                  width: 80,
                  textAlign: 'right',
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 11,
                  color: 'var(--fg-3)',
                }}
              >
                {costOf(entry) === undefined ? '—' : formatAmount(costOf(entry)!)}
              </Box>
              <Box sx={{ width: 80, textAlign: 'right' }}>
                <InlineTextCell
                  automationId="rate-card-rate"
                  value={formatAmount(entry.rate)}
                  editing={isEditing(`rate:${entry.id}`)}
                  onStartEdit={() => startEditing(`rate:${entry.id}`)}
                  onCancel={stopEditing}
                  onCommit={(raw) => {
                    const rate = parseAmountInput(raw);
                    if (rate !== null) {
                      dispatch({
                        type: 'APPLY_OP',
                        op: (document) => ({
                          ...document,
                          rateCard: document.rateCard.map((candidate) =>
                            candidate.id === entry.id ? { ...candidate, rate } : candidate,
                          ),
                        }),
                      });
                    }
                    stopEditing();
                  }}
                  align="right"
                  inputWidth={70}
                  inputSx={{ fontFamily: 'var(--font-family-mono)', fontSize: 11 }}
                  display={
                    <Box
                      component="span"
                      sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, fontWeight: 500 }}
                    >
                      {formatAmount(entry.rate)}
                    </Box>
                  }
                />
              </Box>
              {isTm ? (
                <Box
                  sx={{
                    width: 110,
                    textAlign: 'right',
                    fontFamily: 'var(--font-family-mono)',
                    fontSize: 11,
                    color: 'var(--fg-3)',
                  }}
                  data-automation-id="rate-card-net"
                >
                  {formatAmount(netRate(entry.rate))}
                </Box>
              ) : null}
              <Box
                data-automation-id="rate-card-metric"
                data-metric={metric}
                sx={{
                  width: 64,
                  textAlign: 'right',
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 11,
                  color: 'var(--fg-2)',
                }}
              >
                {/* Over the NET rate: the discount is what the customer actually
                    pays, so it is the uplift the role really earns over its cost. */}
                {formatRateOverCost(metric, netRate(entry.rate), costOf(entry))}
              </Box>
              <Box
                component="button"
                type="button"
                data-automation-id="rate-card-remove"
                onClick={() =>
                  dispatch({
                    type: 'APPLY_OP',
                    op: (document) => removeRateCardEntry(document, entry.id),
                  })
                }
                sx={{
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  bgcolor: 'transparent',
                  color: 'var(--fg-3)',
                  flex: 'none',
                  p: 0,
                  cursor: 'pointer',
                  '&:hover': { color: 'var(--color-coral-80)' },
                }}
              >
                <MaterialSymbol name="close" size={13} />
              </Box>
            </Box>
          ))}

          <Box
            sx={{
              px: 1,
              py: 1,
              borderTop: 'var(--border-width-thin) solid var(--border-divider)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              component="button"
              type="button"
              data-automation-id="rate-card-add"
              disabled={!lookupsEnabled}
              title={lookupsEnabled ? undefined : t('rateCard.AddRoleDisabledTitle')}
              onClick={(event: MouseEvent<HTMLElement>) => setAddAnchor(event.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                border: 'var(--border-width-thin) dashed var(--border-divider)',
                borderRadius: 'var(--border-radius-pill)',
                bgcolor: 'transparent',
                fontSize: 11,
                fontFamily: 'var(--font-family)',
                color: 'var(--fg-3)',
                cursor: lookupsEnabled ? 'pointer' : 'default',
                '&:hover': lookupsEnabled
                  ? { borderColor: 'var(--color-pink-80)', color: 'var(--color-pink-80)' }
                  : {},
              }}
            >
              <MaterialSymbol name="add" size={12} />
              {t('rateCard.AddRole')}
            </Box>
            {fillable.length > 0 ? (
              <Box
                component="button"
                type="button"
                data-automation-id="rate-card-fill-costs"
                title={t('rateCard.FillCostsTitle')}
                onClick={fillCosts}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  border: 'var(--border-width-thin) dashed var(--border-divider)',
                  borderRadius: 'var(--border-radius-pill)',
                  bgcolor: 'transparent',
                  fontSize: 11,
                  fontFamily: 'var(--font-family)',
                  color: 'var(--fg-3)',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'var(--color-pink-80)', color: 'var(--color-pink-80)' },
                }}
              >
                <MaterialSymbol name="payments" size={12} />
                {t('rateCard.FillCosts', { n: fillable.length })}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Collapse>

      <Menu anchorEl={addAnchor} open={addAnchor !== null} onClose={() => setAddAnchor(null)}>
        {roleLookup.isPending ? <MenuItem disabled>{t('rateCard.LoadingRoles')}</MenuItem> : null}
        {roleLookup.data && addableRoles.length === 0 ? (
          <MenuItem disabled>{t('rateCard.AllRolesOnCard')}</MenuItem>
        ) : null}
        {addableRoles.map((item) => (
          <MenuItem
            key={item.serviceId}
            data-automation-id="rate-card-add-option"
            onClick={() => {
              setAddAnchor(null);
              dispatch({
                type: 'APPLY_OP',
                op: (document) => ({
                  ...document,
                  rateCard: [
                    ...document.rateCard,
                    {
                      id: crypto.randomUUID(),
                      roleRef: roleRef(
                        item.serviceId,
                        item.name,
                        new Date().toISOString(),
                        item.costRate,
                      ),
                      rate: item.rate,
                      ...(item.costRate === undefined ? {} : { costRate: item.costRate }),
                    },
                  ],
                }),
              });
            }}
          >
            {item.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
